// auditActions — translate audit_log.action enums into zh-TW labels for
// the /dashboard/audit timeline. Falls back to a humanised form of the
// raw enum when an action isn't in the catalog so we never block on
// adding new audit events to the codebase.
//
// Coverage is enforced by scripts/audit/audit-action-labels-coverage.ts
// (CI). Backend `writeAudit({ action: "<dotted>" })` strings must all
// appear as keys here; drift fails typecheck.

const LABELS: Record<string, string> = {
  // Platform / super_admin
  "platform.impersonate.start":           "切換到客戶單位",
  "platform.impersonate.stop":            "結束客戶單位代理",
  "platform.impersonate.clear":           "結束客戶單位代理",
  "platform.announcement.create":         "發布平台公告",
  // Custom RBAC (P3) — role + platform template management
  "role.create":                          "建立自訂角色",
  "role.update":                          "編輯自訂角色",
  "role.delete":                          "封存自訂角色",
  "role.permissions_replace":             "更新角色權限",
  "platform.role_template.create":        "建立平台角色範本",
  "platform.role_template.update":        "編輯平台角色範本",
  "platform.role_template.delete":        "封存平台角色範本",
  "user.role_assignments_replace":        "更新使用者角色指派",
  "platform.announcement.dispatch":       "發送平台公告通知",
  "platform_announcement.create":         "發布平台公告",
  "platform_announcement.dispatch":       "發送平台公告通知",
  "platform.secret.update":               "更新平台機密設定",
  "platform_secret.delete":               "刪除平台機密設定",
  "platform.me.link_line":                "綁定平台管理員 LINE",
  "super_admin.link_line":                "平台管理員綁定 LINE",
  "platform.ai_quota.update":             "調整客戶 AI 月度上限",
  "platform.ai_grant.issue":              "發出 AI 加值券",
  "platform.ai_grant.revoke":             "撤銷 AI 加值券",

  // Shared Skill Layer — 共用技能層（spec docs/specs/shared-skill-layer.md §10）
  "ai.skill.candidate.created":           "建立共用技能候選",
  "ai.skill.candidate.anonymized":        "候選去識別化完成",
  "ai.skill.candidate.viewed":            "查看技能候選",
  "ai.skill.candidate.edited":            "編輯技能候選",
  "ai.skill.candidate.approved":          "批准技能候選為 active",
  "ai.skill.candidate.rejected":          "拒絕技能候選",
  "ai.skill.candidate.request_changes":   "要求修改技能候選",
  "ai.skill.candidate.blocked":           "將候選標為 blocked（高隱私風險）",
  "ai.skill.candidate.manual_redact":     "手動去識別化覆寫",
  "ai.skill.created":                     "active 共用技能建立",
  "ai.skill.version_bumped":              "共用技能升版本",
  "ai.skill.deprecated":                  "共用技能停用",
  "ai.skill.reactivated":                 "共用技能重啟",
  "ai.skill.retrieved":                   "AI runtime 檢索套用",
  "ai.skill.influenced_plan":             "技能影響 AI plan",
  "ai.skill.blocked_action":              "技能阻擋 AI 動作",
  "ai.skill.required_confirmation":       "技能要求二次確認",
  "ai.skill.evaluation_recorded":         "記錄技能執行結果",
  "ai.skill.embed_failed":                "技能向量索引同步失敗（fail-soft，需 reindex 修補）",

  // Binding codes / invites
  "binding_code.issue":                   "發出邀請碼",
  "binding_code.revoke":                  "收回邀請碼",
  "binding_code.redeem":                  "兌換邀請碼",
  "super_admin_binding_code.issue":       "發出平台管理員邀請碼",
  "super_admin_binding_code.revoke":      "收回平台管理員邀請碼",

  // Clients
  "client.create":                        "建立客戶",
  "client.update":                        "更新客戶資料",
  "client.archive":                       "封存客戶",

  // Sites / floors / buildings / markers / layouts
  "building.create":                      "建立建物",
  "building.update":                      "更新建物",
  "building.archive":                     "封存建物",
  "floor.create":                         "建立樓層",
  "floor.update":                         "更新樓層",
  "floor.archive":                        "封存樓層",
  "marker.create":                        "建立樓層標記",
  "marker.update":                        "更新樓層標記",
  "marker.archive":                       "封存樓層標記",
  "layout.upload":                        "上傳樓層平面圖",
  "layout.create":                        "建立樓層平面圖",
  "layout.edit":                          "編輯樓層平面圖",
  "layout.clear":                         "清除樓層平面圖",
  "layout_version.create":                "新增平面圖版本",
  "layout_version.publish":               "發布平面圖版本",
  "layout.parse.success":                 "平面圖 AI 解析完成",
  "layout.parse.failed":                  "平面圖 AI 解析失敗",
  "site.layout.satellite_captured":       "擷取衛星底圖",
  "site.visual.upgraded":                 "視覺資產升級完成",

  // AI Token Quota (spec docs/specs/ai-token-quota.md §10)
  "ai.quota.exceeded":                    "AI 月配額已達上限",

  // AI 對話 / 工具 / 引導 / 平台監看
  "ai.thread.create":                     "建立 AI 對話",
  "ai.thread.delete":                     "刪除 AI 對話",
  "ai.message.send":                      "發送 AI 對話訊息",
  "ai.cancel":                            "取消 AI 回應串流",
  "ai.feedback":                          "AI 訊息回饋",
  "ai.tool_result":                       "AI 工具回應寫入",
  "ai.tour.start":                        "AI 引導開始",
  "ai.tour.end":                          "AI 引導結束",
  "ai.fact_deleted":                      "刪除 AI 記憶事實",
  "ai.facts_wiped":                       "清空 AI 記憶事實",
  "ai.fact_extracted":                    "AI 從對話抽取記憶事實",
  "ai.platform_state.inspect":            "檢視 AI 平台狀態",

  // Locations + NTAG424 pairing
  "location.create":                    "建立地點",
  "location.update":                    "更新地點",
  "location.retire":                    "封存地點",
  "location.pair":                      "綁定 NFC 標籤",
  "location.rebind":                    "重新綁定 NFC 標籤",
  "location.unbind":                    "解除 NFC 綁定",
  "location.pairing_code.issue":        "發出 NFC 配對碼",

  // Routes (route_locations 表)
  "route.location.auto_added":          "地點自動加入路線（任務派發觸發）",

  // Task assignments (派發)
  "task_assignment.create":               "建立任務派發",
  "task_assignment.update":               "更新任務派發",
  "task_assignment.archive":              "封存任務派發",

  // Officers
  "officer.create":                       "建立巡邏員",
  "officer.update":                       "更新巡邏員",
  "officer.deactivate":                   "停用巡邏員",
  "officer.assign_sites":                 "指派巡邏員至案場",
  "officer.face.enroll":                  "巡邏員人臉登錄",
  "officer.face.revoke":                  "撤銷巡邏員人臉登錄",
  "officer.passkey.enroll":               "巡邏員 Passkey 登錄",
  "officer.passkey.revoke":               "撤銷巡邏員 Passkey",

  // Patrol / scan / tasks
  // scan-wizard-v2 (2026-05+) writes scan-session-level audit actions
  // for identity verify; the older submission-scoped actions stay for
  // backwards-compat audit history and are kept here so old log
  // entries still get a friendly label.
  "patrol.scan.verify.device":            "掃描裝置（Passkey）驗證",
  "patrol.scan.verify.device.fail":       "掃描裝置驗證失敗",
  "patrol.scan.verify.face":              "掃描人臉驗證",
  "patrol.scan.verify.face.fail":         "掃描人臉驗證失敗",
  "patrol.scan.verify.geo":               "掃描位置驗證",
  "patrol.scan.verify.geo.fail":          "掃描位置驗證失敗",
  "patrol.submission.expire":             "巡邏提交逾時自動取消",
  "patrol.submission.respond":            "回覆巡邏提交",
  "patrol.submission.skip":               "跳過巡邏提交",
  "patrol.scan":                          "開始巡邏感應",
  "patrol.scan.match":                    "巡邏感應比對",
  "patrol.scan.finalize":                 "巡邏感應完成",
  "patrol_session.start":                 "開始巡邏勤務",
  "patrol_session.end":                   "結束巡邏勤務",
  // Deprecated (per-submission verify) — PR-G 起 endpoint 回 410 Gone，
  // 不再產生新的此類 audit row；只用來標 PR-G 前歷史紀錄。PR-H
  // (2026-07-31) 之後可整列移除。
  "patrol.submission.verify.face.fail":   "提交人臉驗證失敗（舊路徑，2026-05 已停用）",
  "task.instruction_image.upload":        "上傳任務說明圖片",

  // Incidents
  "incident.create.quick":                "快速通報異常",
  "incident.created":                     "建立異常通報",
  "incident.update":                      "更新異常通報",
  "incident.ack":                         "確認異常通報",
  "incident.assign":                      "指派異常通報",
  "incident.resolve":                     "結案異常通報",
  "incident.escalated":                   "事件已升級",
  "incident.assigned":                    "事件已指派",
  "incident.timeline_viewed":             "查看事件時間軸（敏感存取）",

  // Asset module (modules/asset/) — declared in audit/actions.ts.
  "asset.created":                        "登錄新資產",
  "asset.updated":                        "編輯資產",

  // Module platform — super_admin tenant enablement actions.
  "module.enabled":                       "啟用模組",
  "module.disabled":                      "停用模組",

  // Risk / 風險警示
  "risk.mute":                            "靜音風險警示",

  // Memberships / users
  "membership.update":                    "更新成員角色",
  "membership.revoke":                    "移除成員",
  "user.link_line":                       "綁定使用者 LINE",
  "user.unlink_line":                     "解除使用者 LINE 綁定",

  // LINE
  "line_integration.update":              "更新 LINE 整合",
  "line_integration.disable":             "停用 LINE 整合",
  "line_group.update":                    "更新 LINE 群組",
  "line_group.disable":                   "停用 LINE 群組",
  "line_group.claim":                     "認領 LINE 群組為案場",

  // Notification rules — 通知規則設定
  "notification_rule.create":             "建立通知規則",
  "notification_rule.update":             "更新通知規則",
  "notification_rule.disable":            "停用通知規則",

  // Notification module (event-driven push policy layer) —
  // declared in modules/notification/audit/actions.ts; first
  // emitted from event handlers in D-2.
  "notification.line_pushed":             "已推送 LINE 通知",
  "connector.line.event_dead_letter":     "LINE 事件無對應流程（dead-letter）",
  // LINE 進站對話契約化（path A；notification-api 驗章後內部轉發到 connect-api）
  "line.inbound.mapped":                   "LINE 群組／個人訊息對應為被治理的執行事件",
  "line.inbound.dead_letter":              "LINE 進站訊息無對應規則（dead-letter，已紀錄不丟棄）",
  "line.inbound.ignored":                  "LINE 進站訊息刻意略過（宣告的 no-op）",
  // LINE AI 理解能力（stage ii；只提案、AI 絕不執行，由人核准 stage iii）
  "line.proposal.created":                 "AI 讀已認領群組對話產出一份提案草稿（建議，非執行；待人核准）",
  "line.proposal.approved":                "核准人核准 AI 提案（stage iii；人核准後 runtime 才執行）",
  "line.proposal.rejected":                "核准人拒絕 AI 提案（stage iii；不執行）",
  "line.proposal.executed":                "AI 提案經人核准後由 runtime 確定性執行（stage iii）",
  "line.proposal.execution_failed":        "AI 提案經人核准、但執行的連接器動作失敗（fail-closed；未偽造成功、未標記為已執行）",
  "line.proposal.timed_out":               "AI 提案核准逾時，依規則處理（stage iii；逾時絕不暗自當核准）",
  // Connect-family 連接器（connect-api；「有系統就整合」）
  "connector.installation.created":        "安裝外部系統連接器",
  "connector.installation.activated":      "啟用外部系統連接器",
  "connector.installation.disabled":       "停用外部系統連接器",
  "connector.credential.set":              "設定連接器憑證（只記錄參照名稱與遮罩提示，永不記錄憑證值）",
  "connector.github.event_mapped":         "GitHub 事件對應為被治理的執行事件",
  "connector.github.event_dead_letter":    "GitHub 事件無對應流程（dead-letter，已紀錄不丟棄）",
  "connector.github.event_ignored":        "GitHub 事件刻意略過（宣告的 no-op）",
  "connector.github.event_failed":         "GitHub 事件處理失敗",
  "connector.github.action_dispatched":    "對 GitHub 發出對外動作",
  "connector.github.action_failed":        "對 GitHub 的對外動作失敗",
  "connector.slack.event_mapped":          "Slack 事件對應為被治理的執行事件",
  "connector.slack.event_dead_letter":     "Slack 事件無對應流程（dead-letter，已紀錄不丟棄）",
  "connector.slack.event_ignored":         "Slack 事件刻意略過（宣告的 no-op）",
  "connector.slack.event_failed":          "Slack 事件處理失敗",
  "connector.slack.action_dispatched":     "對 Slack 發出對外動作",
  "connector.slack.action_failed":         "對 Slack 的對外動作失敗",
  "connector.telegram.event_mapped":       "Telegram 事件對應為被治理的執行事件",
  "connector.telegram.event_dead_letter":  "Telegram 事件無對應流程（dead-letter，已紀錄不丟棄）",
  "connector.telegram.event_ignored":      "Telegram 事件刻意略過（宣告的 no-op）",
  "connector.telegram.event_failed":       "Telegram 事件處理失敗",
  "connector.telegram.action_dispatched":  "對 Telegram 發出對外動作",
  "connector.telegram.action_failed":      "對 Telegram 的對外動作失敗",
  "connector.oauth.authorize_started":     "開始外部系統 OAuth 授權（取得使用者同意；僅記錄供應商與權限範圍，永不記錄權杖）",
  "connector.oauth.authorized":            "完成外部系統 OAuth 授權並取得權杖（權杖加密保存，永不記錄權杖值）",
  "connector.oauth.exchange_failed":       "外部系統 OAuth 授權碼換取權杖失敗",
  "connector.oauth.refreshed":             "更新外部系統 OAuth 存取權杖（自動續期，永不記錄權杖值）",
  "connector.oauth.refresh_failed":        "外部系統 OAuth 權杖續期失敗（fail-closed：拒絕該次動作，不偽造呼叫）",
  "connector.google_calendar.action_dispatched": "對 Google 日曆發出對外動作（建立行程）",
  "connector.google_calendar.action_failed":     "對 Google 日曆的對外動作失敗",
  "connector.google_calendar.event_received":    "排程輪詢 Google 日曆收到行程異動（依事件 ID＋更新時間去重，只取行程中繼資料）",
  "connector.google_calendar.poll_read_failed":  "排程輪詢 Google 日曆讀取失敗（no-op，不偽造空白輪詢）",
  "connector.gmail.action_dispatched":           "對 Gmail 發出對外動作（寄送郵件）",
  "connector.gmail.action_failed":               "對 Gmail 的對外動作失敗",
  "connector.gmail.message_received":            "排程輪詢 Gmail 收到新信（只取信件中繼資料，永不讀信件內文）",
  "connector.gmail.poll_read_failed":            "排程輪詢 Gmail 讀取失敗（no-op，不偽造空白輪詢）",
  "connector.google_drive.action_dispatched":    "對 Google 雲端硬碟發出對外動作（上傳／分享檔案）",
  "connector.google_drive.action_failed":        "對 Google 雲端硬碟的對外動作失敗",
  "connector.google_drive.event_received":       "排程輪詢 Google 雲端硬碟收到檔案異動（依檔案 ID＋修改時間去重，只取檔案中繼資料，永不讀檔案內容）",
  "connector.google_drive.poll_read_failed":     "排程輪詢 Google 雲端硬碟讀取失敗（no-op，不偽造空白輪詢）",
  "connector.google_sheets.action_dispatched":   "對 Google 試算表發出對外動作（新增／更新列、匯出／批次匯入）",
  "connector.google_sheets.action_failed":       "對 Google 試算表的對外動作失敗",
  "connector.google_sheets.import_completed":    "完成 Google 試算表／CSV 批次匯入（只記錄列數，永不記錄欄位內容）",
  "connector.google_sheets.sheet_synced":        "排程同步 Google 試算表工作表的資料列（依列鍵＋內容雜湊去重）",
  "connector.google_sheets.sync_read_failed":    "排程同步讀取 Google 試算表失敗（no-op，不偽造空白同步）",
  "connector.notion.action_dispatched":          "對 Notion 發出對外動作（建立頁面／附加區塊）",
  "connector.notion.action_failed":              "對 Notion 的對外動作失敗",
  "connector.notion.event_received":             "排程輪詢 Notion 收到頁面／資料庫異動（依物件 ID＋最後編輯時間去重，只取物件中繼資料，永不讀頁面內容）",
  "connector.notion.poll_read_failed":           "排程輪詢 Notion 讀取失敗（no-op，不偽造空白輪詢）",
  "connector.jira.event_mapped":                 "Jira 事件對應為被治理的執行事件",
  "connector.jira.event_dead_letter":            "Jira 事件無對應流程（dead-letter，已紀錄不丟棄）",
  "connector.jira.event_ignored":                "Jira 事件刻意略過（宣告的 no-op）",
  "connector.jira.event_failed":                 "Jira 事件處理失敗",
  "connector.jira.action_dispatched":            "對 Jira 發出對外動作（建立議題／轉換狀態）",
  "connector.jira.action_failed":                "對 Jira 的對外動作失敗",
  "connector.trello.event_mapped":               "Trello 事件對應為被治理的執行事件",
  "connector.trello.event_dead_letter":          "Trello 事件無對應流程（dead-letter，已紀錄不丟棄）",
  "connector.trello.event_ignored":              "Trello 事件刻意略過（宣告的 no-op）",
  "connector.trello.event_failed":               "Trello 事件處理失敗",
  "connector.trello.action_dispatched":          "對 Trello 發出對外動作（建立卡片）",
  "connector.trello.action_failed":              "對 Trello 的對外動作失敗",
  "connector.teams.action_dispatched":           "對 Microsoft Teams 發出對外動作（張貼訊息）",
  "connector.teams.action_failed":               "對 Microsoft Teams 的對外動作失敗",
  "connector.teams.event_mapped":                "Teams 互動事件對應為被治理的執行事件（核准卡片按鈕）",
  "connector.teams.event_dead_letter":           "Teams 互動事件無對應流程（dead-letter，已紀錄不丟棄）",
  "connector.teams.event_ignored":               "Teams 互動事件刻意略過（宣告的 no-op）",
  "connector.teams.event_failed":                "Teams 互動事件處理失敗",
  "connector.discord.action_dispatched":         "對 Discord 發出對外動作（張貼訊息／改寫／回應互動）",
  "connector.discord.action_failed":             "對 Discord 的對外動作失敗",
  "connector.discord.event_mapped":              "Discord 互動事件對應為被治理的執行事件（核准卡片按鈕）",
  "connector.discord.event_dead_letter":         "Discord 互動事件無對應流程（dead-letter，已紀錄不丟棄）",
  "connector.discord.event_ignored":             "Discord 互動事件刻意略過（宣告的 no-op）",
  "connector.discord.event_failed":              "Discord 互動事件處理失敗",
  "connector.shopline.event_mapped":             "Shopline 事件對應為被治理的執行事件",
  "connector.shopline.event_dead_letter":        "Shopline 事件無對應流程（dead-letter，已紀錄不丟棄）",
  "connector.shopline.event_ignored":            "Shopline 事件刻意略過（宣告的 no-op）",
  "connector.shopline.event_failed":             "Shopline 事件處理失敗",
  "connector.shopline.action_dispatched":        "對 Shopline 發出對外動作（建立訂單／更新庫存）",
  "connector.shopline.action_failed":            "對 Shopline 的對外動作失敗",
  "connector.meta.event_mapped":                 "Meta 事件對應為被治理的執行事件（IG／Messenger 訊息、貼文留言、名單廣告）",
  "connector.meta.event_dead_letter":            "Meta 事件無對應流程（dead-letter，已紀錄不丟棄）",
  "connector.meta.event_ignored":                "Meta 事件刻意略過（宣告的 no-op）",
  "connector.meta.event_failed":                 "Meta 事件處理失敗",
  "connector.meta.action_dispatched":            "對 Meta 發出對外動作（傳送訊息／加上客戶標籤）",
  "connector.meta.action_failed":                "對 Meta 的對外動作失敗",
  "connector.shopee.event_mapped":               "Shopee 事件對應為被治理的執行事件（訂單／庫存／退貨）",
  "connector.shopee.event_dead_letter":          "Shopee 事件無對應流程（dead-letter，已紀錄不丟棄）",
  "connector.shopee.event_ignored":              "Shopee 事件刻意略過（宣告的 no-op）",
  "connector.shopee.event_failed":               "Shopee 事件處理失敗",
  "connector.shopee.action_dispatched":          "對 Shopee 發出對外動作（查訂單／查商品／更新庫存／出貨／取消訂單／處理退貨）",
  "connector.shopee.action_failed":              "對 Shopee 的對外動作失敗",
  "forge.package.imported":               "匯入 FORGE Package（已驗章）",
  "forge.package.rejected":               "FORGE Package 驗證失敗（拒收）",
  "forge.definitions.landed":             "FORGE Package 落地為租戶定義",
  "runtime.workflow.instantiated":        "啟動營運藍圖流程實例",
  "runtime.state.advanced":               "流程實例推進一步（狀態轉換）",
  "runtime.advance.paused":               "流程推進暫停（守衛條件無法確定性判定）",
  "runtime.advance.guard_not_satisfied":  "流程推進未發生（守衛條件不成立）",
  "runtime.verification.recorded":        "記錄驗證判定（由確定性信任驗證層判定，非 AI）",
  "runtime.verification.blocked":         "流程推進被驗證閘擋下（判定未通過或保證強度不足，預設拒絕）",
  "runtime.evidence.recorded":            "記錄驗證證據參照（R2 物件參照，非原始位元組）",
  "runtime.capability.invoked":           "呼叫綁定的能力供應商並記錄結果（供應商執行、本層只記錄，非偽造成功）",
  "runtime.capability.failed":            "能力呼叫未成功（被流程依賴的必需能力呼叫未成功，預設拒絕推進）",
  // 互動式核准（非同步審核回呼）— interactive-approval callback contract §6/§9/§10
  "runtime.approval.pending":             "流程暫停等待核准，已送出核准卡片（耐久等待，非即時連線）",
  "runtime.approval.decided":             "核准人按下決定（核准/拒絕），逐字記錄人的判定（非 AI 判定）並繼續流程",
  "runtime.approval.unauthorized":        "非授權核准人嘗試決定，已擋下、未變更狀態",
  "runtime.approval.timed_out":           "核准逾時，依藍圖規則處理（逾時一律不視為核准、預設拒絕或保留）",
  "runtime.approval.escalated":           "核准逾時並升級給上層核准人（開啟新的核准卡片）",
  "runtime.approval.cancelled":           "原請求撤回，關閉待核准並更新卡片為已取消",
  "runtime.approval.card_edit_failed":    "核准結果卡片更新失敗（已記錄待重試，狀態以待核准列與判定為準）",
  "data.external_reference.looked_up":    "查詢外部參考資料成功（天氣／匯率等唯讀公開資料；只記錄資料類別與結果，永不記錄查得的數值或密鑰）",
  "data.external_reference.unavailable":  "外部參考資料無法取得（上游錯誤／逾時／輸入無效，回報無法取得而非捏造資料）",
  "notification.suppressed":              "通知已抑制（未推送）",
  "notification.dispatch_failed":         "通知推送失敗",
  // 待審核 MCP 工具呼叫的「推播給核准者」通知（push-to-pull；附帶後台連結，
  // 不能直接核准／拒絕）。docs/specs/nexus-mcp-approval-audit-v0.md §9 (b)。
  "notification.mcp_approval.dispatched": "已推播 MCP 待審核通知給核准者",

  // Map import — 樓層平面圖匯入流程
  "map_import.create":                    "建立樓層平面圖匯入",
  "map_import.retry":                     "重試樓層平面圖匯入",
  "map_import.approve":                   "核准樓層平面圖匯入",
  "map_import.cancel":                    "取消樓層平面圖匯入",
  "map_import.cleaned_image.upload":      "上傳清理後平面圖",

  // Tenants / sites / routes / tasks — admin CRUD
  "tenant.create":                        "建立客戶單位",
  "tenant.update":                        "更新客戶單位",
  "tenant.suspend":                       "停用客戶單位",
  "site.create":                          "建立案場",
  "site.update":                          "更新案場",
  "site.archive":                         "封存案場",
  "route.create":                         "建立路線",
  "route.update":                         "更新路線",
  "route.archive":                        "封存路線",
  "route.locations.replace":            "重新編排路線地點",
  "task.create":                          "建立任務",
  "task.update":                          "更新任務",
  "task.archive":                         "封存任務",

  // Schedule — 班別交換 / 排班操作
  "shift.archive":                        "封存班別",
  "shift.handoff.ack":                    "確認班別交接",
  "swap_request.create":                  "申請班別交換",
  "roster.create":                        "建立排班",
  "roster.update":                        "更新排班",
  "roster.cancel":                        "取消排班",
  "roster.generate":                      "自動產生排班",

  // Floor stack snapshots
  "floor_status.snapshot":                "樓層狀態快照",

  // Billing / invoices
  "invoice.render_pdf":                   "重新產生發票 PDF",
  "invoice.download":                     "下載發票 PDF",

  // Schedule
  "shift.create":                         "建立班別",
  "shift.update":                         "更新班別",
  "shift.delete":                         "刪除班別",
  "roster_entry.create":                  "建立排班",
  "roster_entry.update":                  "更新排班",
  "roster_entry.delete":                  "取消排班",
  "schedule_exception.create":            "建立排班例外",
  "schedule_exception.update":            "更新排班例外",
  "schedule_exception.delete":            "刪除排班例外",
  "swap_request.approved":                "核准班別交換",
  "swap_request.rejected":                "駁回班別交換",
  "swap_request.cancelled":               "取消班別交換",

  // MCP read-only access (docs/specs/mcp-read-only.md §7.2 + §13)
  "mcp.tool.invoked":                     "MCP 工具呼叫",
  "mcp.key.created":                      "建立 MCP 存取金鑰",
  "mcp.key.revoked":                      "撤銷 MCP 存取金鑰",
  "mcp.oauth.client_registered":          "OAuth client 註冊（DCR）",
  "mcp.oauth.authorized":                 "授權 OAuth client 存取",
  "mcp.oauth.token_issued":               "發出 OAuth access + refresh token",
  "mcp.oauth.token_refreshed":            "OAuth token 自動續期",
  "mcp.oauth.token_revoked":              "撤銷 OAuth token（單筆）",
  "mcp.oauth.consent_revoked":            "撤銷 OAuth client 整組授權",
  "mcp.oauth.code_reused":                "OAuth 授權碼被重放（安全事件）",
  "mcp.oauth.refresh_reused":             "OAuth refresh token 被重放（安全事件）",
  "mcp.tool_call.approved":               "核准 MCP 工具呼叫",
  "mcp.tool_call.rejected":               "拒絕 MCP 工具呼叫",
  "mcp.tool_call.executed":               "MCP 工具呼叫核准後執行",
  "mcp.tool_call.execute_failed":         "MCP 工具呼叫執行失敗",
  "mcp.tool_call.execute_abandoned":      "MCP 工具呼叫重試耗盡放棄",
  "mcp.tool_call.timed_out":              "MCP 工具呼叫核准逾時",
  "mcp.tool_projection.created":          "建立 MCP 工具可見性覆蓋",
  "mcp.tool_projection.updated":          "修改 MCP 工具可見性覆蓋",
  "mcp.tool_projection.deleted":          "刪除 MCP 工具可見性覆蓋",
  // Context Foundation（SEN-506）＋ Lite Vertical Slice（SEN-498）——
  // Personal Space 的 context 稽核（context_audit_events；dashboard 為
  // Organization Context 專用，正常不顯示這些列，標籤仍需齊備防 drift）
  "context.personal_space.created":       "建立 Personal Space",
  "context.organization.mapped":          "映射既有單位為 Organization Context",
  "context.access.denied":                "跨 context 存取被拒絕",
  "personal.calendar.connected":          "連接個人 Google 日曆",
  "personal.plan.created":                "NEXUS 擬定個人執行計畫（等待確認）",
  "personal.plan.executed":               "確認個人執行計畫並建立待辦",
  "personal.plan.cancelled":              "取消個人執行計畫",
};

/** Human-readable zh-TW label for an audit action enum. */
export function actionLabel(action: string): string {
  if (LABELS[action]) return LABELS[action];

  // Fallback humanizer: split on dots, replace underscores with spaces,
  // capitalize. e.g. "site.archive" → "site • archive";
  // "officer.face.enroll" → "officer • face enroll".
  const parts = action.split(".");
  if (parts.length === 0) return action;
  return parts
    .map((p) => p.replace(/_/g, " "))
    .join(" ‧ ");
}
